import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Logo } from '@/components/brand/logo'
import { Wordmark } from '@/components/brand/wordmark'
import { SearchBar } from '@/components/ui/search-bar'
import { HistoryStack } from '@/components/ui/history-stack'
import { UserStatusSelector } from '@/components/users'
import { UserMenu } from '@/components/layout/UserMenu'
import { useTheme } from '@/hooks/useTheme'
import { useAuth } from '@/hooks/useAuth'
import { UserStatus } from '@/types'

export function Header() {
  const { theme, setTheme } = useTheme()
  const { user, logout, refreshUser } = useAuth()
  const navigate = useNavigate()

  const handleLogout = async () => {
    try {
      await logout()
      toast.success('Logged out successfully')
      navigate('/login')
    } catch {
      toast.error('Failed to log out')
    }
  }

  const handleStatusChange = async (_newStatus: UserStatus) => {
    // Status update is handled by UserStatusSelector component
    // We just need to refresh the user object in context without page reload
    await refreshUser()
  }

  return (
    <header className="w-full border-b border-b-gray-200 bg-gray-50 dark:bg-gray-900 dark:border-b-gray-700">
      <div className="flex h-16 items-center justify-between px-7 gap-3">
        <div className="flex items-center">
          <div className="flex items-center gap-2">
            <Link to="/" className="color-black dark:color-white">
              {/* Show Wordmark on mobile/tablet, Logo on desktop */}
              <div className="block sm:hidden">
                <Wordmark className="h-6 w-auto" />
              </div>
              <div className="hidden sm:block">
                <Logo className="h-6 w-auto" />
              </div>
            </Link>
            <span className="text-xl font-bold sr-only">Axioma</span>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-1 justify-center">
          <HistoryStack />
          <SearchBar />
        </div>

        <div className="flex items-center gap-2">
          {user && (
            <UserStatusSelector
              user={user}
              onStatusChange={handleStatusChange}
              onRefreshUser={refreshUser}
              variant="auto"
              size="md"
            />
          )}

          {user && (
            <UserMenu
              user={user}
              theme={theme}
              onThemeChange={(theme: string) => setTheme(theme as 'dark' | 'light' | 'system')}
              onLogout={handleLogout}
            />
          )}
        </div>
      </div>
    </header>
  )
}
